import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Map } from 'immutable';
import { Route, Switch, useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import config from 'react-global-configuration';
import useCookie from 'react-use-cookie';
import { Toaster } from 'react-hot-toast';

import {
  I18nProvider,
  EntityMapperContext,
  ContextualActionsWrapper,
  commerceCartSetToken,
  commerceLoadCart,
} from '@uncinc/react-drupal-core';

import {
  KitchenSinkComponentMapperContext,
  useWindowDimensions,
} from '@uncinc/uncinc-react-kitchen-sink';

import blocksMapper from '../../mappers/blocks';
import nodeMapper from '../../mappers/node';
import paragraphMapper from '../../mappers/paragraphs';
import taxonomyMapper from '../../mappers/taxonomy';
import viewsMapper from '../../mappers/views';
import i18next from '../../config/i18next';
import Header from '../../components/Header';
import Cart from '../Cart';
import CartCheckout from '../Cart/Checkout';
import CheckoutPageV2 from '../Cart/Checkout/CheckoutV2';
import CartCheckoutConfirm from '../Cart/Checkout/Confirm';
import Entity from '../Entity';

import { DOMAIN_KVNL, IS_CLIENT, SCREEN_TABLET, __DEBUG__, URL_SSO_FLOW_IDENTIFIER, KINDERVEILIGHEID_TIKTOK_URL, GTM_SOURCE_LOGIN } from '../../config/constants';

import SearchkitSearch from '../SearchkitSearch';
import OrderOverview from '../Order/Overview';
import OrderDetail from '../Order/Detail';

import UserLogin from '../User/Login';
import UserRegister from '../User/Register';
import SsoInfo from '../User/SsoInfo';
import UserAccountPage from '../User/Account';
import UserResetPassword from '../User/ResetPassword';
import UserRequestPassword from '../User/RequestPassword';
import UserSuccess from '../User/Success';
import UserCheck from '../User/CheckUser';
import SpotlerSubscription from '../SpotlerSubscription';

import UserGrantRiskMonitor from '../User/RiskMonitor/GrantRiskMonitor';
import ActivateElearning from '../User/ActivateElearning';

import ComponentenTest from '../ComponentenTest';
import Success from '../Cart/Checkout/Success';
import DesignSystem from '../DesignSystem';

import Footer from '../../implementations/Footer';
import FooterMinimal from '../../implementations/FooterMinimal';
import CheckoutLoginOrRegister from '../Cart/Checkout/LoginOrRegister';

import Pager from '../../components/Pager';
import SkipToMainContentLink from '../../components/SkipToMainContentLink';
import TikTokFeed from '../../components/TikTokFeed';
import AnimatedPartners from '../../components/AnimatedPartners';

import {
  ROUTE_PASSWORD_REQUEST,
  ROUTE_FORM_QUIZ,
  ROUTE_RESULT,
  ROUTE_NEWSLETTER_SUBSCRIBE,
  ROUTE_USER_CHECK,
  ROUTE_CHECKOUT_V2,
  ROUTE_ACCOUNT_LOGIN,
  ROUTE_ACOUNT_LOGIN_EXTRA_INFO,
  ROUTE_ACCOUNT_REGISTER,
  ROUTE_ELEARNING_ACTIVATE,
} from '../../config/routes';
import { setDeviceSize, setPageViewSent } from '../../store/actions/UI';
import { usePrevious } from '../../helpers/usePrevious';
import { sendPageView } from '../../helpers/gtm';
import CustomRoute from '../CustomRoute';
import ParallaxProvider from '../../components/ParallaxProvider';

import './style.scss';
import '../../styles/main.scss';

const entityTypeMapper = {
  block: blocksMapper,
  node: nodeMapper,
  paragraph: paragraphMapper,
  taxonomy: taxonomyMapper,
  view: viewsMapper,
};

const componentsMapping = {
  Pagination: Pager,
};

const App = () => {
  const pageRef = useRef(null);
  const [currentPath, setCurrentPath] = useState(null);
  const pageViewSent = useSelector((state) => state.getIn(['uiInternal', 'pageViewSent'], false));
  const pageInformation = useSelector((state) => state.getIn(['uiInternal', 'pageInformation'], null));
  const previousPageInformation = usePrevious(pageInformation) || null;
  const path = useSelector((state) => state.getIn(['router', 'location', 'pathname']));
  const entityId = useSelector((state) => state.getIn(['entity', 'entityId'], null));
  const entityType = useSelector((state) => state.getIn(['entity', 'entityType'], null));
  const entityStatus = useSelector((state) => state.getIn(['entity', 'status'], null));
  const childEntity = useSelector((state) => state.getIn([entityType, entityId], Map()));
  const rootClasses = useSelector((state) => state.getIn(['ui', 'rootClasses']));
  const language = useSelector((state) => state.getIn(['ui', 'language']));
  const entityExists = useSelector((state) => state.getIn(['entity', 'exists']));
  const [cartTokenCookie, setCartTokenCookie] = useCookie('commerce-cart-token');
  const cartToken = useSelector((state) => state.getIn(['commerce', 'cart', 'token']));
  const dispatch = useDispatch();
  const referenceLoading = useSelector((state) => state.getIn(['ui', 'referenceLoading']));
  const { width } = useWindowDimensions(true);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const urlParams = new URLSearchParams(window.location.search);
  const isResultPage = path?.includes(ROUTE_RESULT);
  const isMinimalPage = urlParams.get(URL_SSO_FLOW_IDENTIFIER) !== null || isResultPage;

  useEffect(() => {
    if (
      location &&
      location.pathname !== currentPath &&
      (pageInformation?.title && pageInformation?.title !== previousPageInformation?.title) &&
      pageInformation !== null &&
      (pageInformation !== previousPageInformation) &&
      !pageViewSent
    ) {
      sendPageView(
        currentPath,
        location.pathname,
        pageInformation?.title,
        pageInformation,
      );
      dispatch(setPageViewSent(true));
      setCurrentPath(location.pathname);
    }

  }, [pageInformation]);

  useEffect(() => {
    if (IS_CLIENT) {
      const isMobile = width < SCREEN_TABLET;
      dispatch(setDeviceSize({ width, isMobile }));
    }
  }, [width]);

  // Set the commerce cart token in the state, or generate a one when not set.
  useEffect(() => {
    if (cartTokenCookie) {
      if (cartTokenCookie !== cartToken) {
        dispatch(commerceCartSetToken(cartTokenCookie));
        dispatch(commerceLoadCart());
      }
    } else {
      const newToken = Math.random().toString(36).substr(2);
      setCartTokenCookie(newToken, { days: 365 });
    }
  }, [setCartTokenCookie, dispatch, cartTokenCookie, cartToken]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [path]);

  const loadingStatus = useSelector((state) => state.getIn(['entity', 'loadingStatus']));
  const loaded = referenceLoading.get('itemsStarted') <= referenceLoading.get('itemsFinished');
  const ENTITY_LOADING_STATUS_START = 'entity-loading-status-start-delayed';
  const ENTITY_LOADING_STATUS_LOADING = 'entity-loading-status-loading';
  const ENTITY_LOADING_STATUS_LOADED = 'entity-loading-status-loaded';

  const domainIsKVNL = config.get('CURRENT_DOMAIN') === DOMAIN_KVNL;

  const shouldUseAnimation = false;

  const animationClasses = classNames('App', {
    'App-appear': ((entityStatus !== 404) && (!shouldUseAnimation && loadingStatus === ENTITY_LOADING_STATUS_START || !shouldUseAnimation && loadingStatus === ENTITY_LOADING_STATUS_LOADING) || (!shouldUseAnimation && !loaded)),
    'App-appear-done': (!shouldUseAnimation && loadingStatus === ENTITY_LOADING_STATUS_LOADED && loaded === true) || entityStatus === 404,

    'App-loading-start': (loadingStatus === ENTITY_LOADING_STATUS_START),
    'App-loading': ((shouldUseAnimation && loadingStatus === ENTITY_LOADING_STATUS_LOADING) || (shouldUseAnimation && !loaded)),
    'App-loading-loaded': (shouldUseAnimation && loadingStatus === ENTITY_LOADING_STATUS_LOADED && loaded === true),
  });

  const classes = classNames('App', rootClasses.join(' '), 'theme', `theme--${config.get('CURRENT_DOMAIN')}`, {
    'is-front-page': childEntity.get('is_front'),
    'not-found': !entityExists,
    'is-minimal-page': isMinimalPage,
  });

  const renderPage = () => {
    return <Entity />;
  };

  return (
    <I18nProvider locale={language} i18next={i18next()}>
      <EntityMapperContext.Provider value={entityTypeMapper}>
        <KitchenSinkComponentMapperContext.Provider value={componentsMapping}>
          <ParallaxProvider>
            <ContextualActionsWrapper>
              <div ref={pageRef} className={animationClasses}>
                <div>
                  <div className={classes} language={language}>
                    <SkipToMainContentLink />
                    {(!isMinimalPage) && (
                      <Header pageRef={pageRef} />
                    )}
                    <main className="main">
                      <Switch>
                        <CustomRoute exact path={ROUTE_NEWSLETTER_SUBSCRIBE} render={() => (<SpotlerSubscription subscriptionSource={GTM_SOURCE_LOGIN} />)} />
                        <CustomRoute exact path={ROUTE_ACCOUNT_LOGIN} render={() => (<UserLogin />)} />
                        <CustomRoute exact path={ROUTE_ACOUNT_LOGIN_EXTRA_INFO} render={() => (<SsoInfo />)} />
                        <CustomRoute exact path={ROUTE_ACCOUNT_REGISTER} render={() => (<UserRegister />)} />
                        <CustomRoute exact path={ROUTE_USER_CHECK} render={() => (<UserCheck />)} />
                        <CustomRoute exact path={ROUTE_PASSWORD_REQUEST} render={() => (<UserRequestPassword />)} />
                        <CustomRoute exact path="/cart" render={() => (<Cart />)} />
                        <CustomRoute privateRoute exact path="/cart/checkout" render={() => (<CartCheckout />)} />
                        <CustomRoute privateRoute exact path={ROUTE_CHECKOUT_V2} render={() => (<CheckoutPageV2 />)} />
                        <CustomRoute exact path="/cart/checkout/login-or-register" render={() => (<CheckoutLoginOrRegister />)} />
                        <CustomRoute exact path="/cart/checkoutv2/login-or-register" render={() => (<CheckoutLoginOrRegister />)} />
                        <CustomRoute privateRoute exact path="/cart/checkout/confirm" render={() => (<CartCheckoutConfirm />)} />
                        <CustomRoute privateRoute exact path="/cart/checkout/success" render={() => (<Success />)} />
                        <CustomRoute exact path="/orders" render={() => (<OrderOverview />)} />
                        <CustomRoute exact path="/order/:order_number" render={() => (<OrderDetail />)} />
                        <CustomRoute exact path="/success/:type" render={() => (<UserSuccess />)} />
                        <CustomRoute exact path="/componenten" render={() => (<ComponentenTest />)} />
                        <CustomRoute exact path="/zoeken" render={() => (<SearchkitSearch />)} />
                        <CustomRoute path={ROUTE_ELEARNING_ACTIVATE} render={() => (<ActivateElearning />)} />
                        <CustomRoute path="/password/reset" render={() => (<UserResetPassword path={path} />)} />
                        {/* Account - Sub-pages (most specific, must come first) */}
                        <CustomRoute privateRoute path="/account/:tab/:id/:subpage" render={() => (<UserAccountPage />)} />
                        {/* Account - Detail pages */}
                        <CustomRoute privateRoute path="/account/:tab/:id" render={() => (<UserAccountPage />)} />
                        {/* Account - List pages */}
                        <CustomRoute privateRoute path="/account/:tab" render={() => (<UserAccountPage />)} />
                        {/* Risico monitor */}
                        <CustomRoute exact path="/risico-monitor/grant" render={() => (<UserGrantRiskMonitor />)} />
                        {__DEBUG__ && <CustomRoute path="/design-system" render={() => (<DesignSystem />)} />}
                        <Route render={() => renderPage()} />
                      </Switch>
                      {!isHomePage && domainIsKVNL && <TikTokFeed url={KINDERVEILIGHEID_TIKTOK_URL} />}
                      {!isHomePage && domainIsKVNL && <AnimatedPartners />}
                    </main>
                    {!(path.startsWith(ROUTE_FORM_QUIZ) || isResultPage) && (
                      isMinimalPage
                        ? <FooterMinimal />
                        : <Footer />
                    )}
                    <Toaster
                      position="top-center"
                      containerClassName="Alert"
                      toastOptions={{
                        duration: 4000,
                      }}
                    />
                    <div id="modal-root" />
                  </div>
                </div>
              </div>
            </ContextualActionsWrapper>
          </ParallaxProvider>
        </KitchenSinkComponentMapperContext.Provider>
      </EntityMapperContext.Provider>
    </I18nProvider>
  );
};

export default App;
